import { forwardRef, useMemo } from 'react';
import Select from 'react-select';

const selectStyles = {
  control: (base, state) => ({
    ...base,
    minHeight: 46,
    borderRadius: 12,
    borderColor: state.isFocused ? 'var(--border-focus)' : 'var(--input-border)',
    boxShadow: state.isFocused ? '0 0 0 3px rgba(96, 165, 250, 0.15)' : 'none',
    // 16px minimo: por debajo de esto iOS Safari hace zoom automatico al enfocar.
    fontSize: 16,
    backgroundColor: 'var(--input-bg)',
    color: 'var(--input-text)',
    '&:hover': {
      borderColor: state.isFocused ? 'var(--border-focus)' : 'var(--border)',
    },
  }),
  menu: (base) => ({
    ...base,
    zIndex: 50,
    backgroundColor: 'var(--bg-surface)',
    color: 'var(--text-primary)',
    borderRadius: 12,
    boxShadow: '0 10px 30px rgba(0,0,0,0.15)',
  }),
  menuPortal: (base) => ({
    ...base,
    zIndex: 9999,
  }),
  option: (base, state) => ({
    ...base,
    backgroundColor: state.isSelected
      ? 'var(--color-brand)'
      : state.isFocused
        ? 'var(--bg-surface-alt)'
        : 'var(--bg-surface)',
    color: state.isSelected ? 'white' : 'var(--text-primary)',
    fontSize: 14,
  }),
  menuList: (base) => ({
    ...base,
    backgroundColor: 'var(--bg-surface)',
    color: 'var(--text-primary)',
  }),
  placeholder: (base) => ({
    ...base,
    color: 'var(--text-secondary)',
  }),
  singleValue: (base) => ({
    ...base,
    color: 'var(--input-text)',
  }),
  input: (base) => ({
    ...base,
    color: 'var(--input-text)',
  }),
  dropdownIndicator: (base) => ({
    ...base,
    color: 'var(--text-secondary)',
  }),
  indicatorSeparator: (base) => ({
    ...base,
    backgroundColor: 'var(--border)',
  }),
};

const getBarrioName = (barrio) => {
  if (typeof barrio === 'string') return barrio;
  return barrio?.nombre || '';
};

const normalizeOptions = (barrios = []) => {
  const seen = new Set();
  return barrios.reduce((options, barrio) => {
    const nombre = getBarrioName(barrio).trim();
    if (!nombre || seen.has(nombre)) return options;
    seen.add(nombre);
    options.push({ value: nombre, label: nombre });
    return options;
  }, []);
};

const BarrioSelect = forwardRef(
  (
    {
      value,
      onChange,
      onBlur,
      options,
      name,
      inputId,
      placeholder = 'Buscar y seleccionar barrio...',
      isClearable = true,
    },
    ref
  ) => {
    const barrioOptions = useMemo(() => normalizeOptions(options), [options]);
    const selectedOption = barrioOptions.find((option) => option.value === value) || null;

    return (
      <Select
        ref={ref}
        inputId={inputId || name}
        name={name}
        value={selectedOption}
        options={barrioOptions}
        onChange={(option) => onChange(option?.value || '')}
        onBlur={onBlur}
        placeholder={placeholder}
        isClearable={isClearable}
        isSearchable
        noOptionsMessage={() => 'No hay barrios disponibles'}
        styles={selectStyles}
        menuPortalTarget={typeof document !== 'undefined' ? document.body : undefined}
      />
    );
  }
);

export default BarrioSelect;
